import React, { useState, useCallback } from 'react'
import { Dialog, Tabs, Flex, Text } from 'components/ds'
import { saveBriefSettings } from '@disco/state-management/reducers/entities/briefs'
import { useDispatch } from 'hooks/use-redux'
import BriefNotes from '../brief-notes'
import NotificationsTab from './notifications-tab'
import type { NotificationFormData } from './notifications-tab'
import styles from './styles.module.css'
import { pushUINotification } from '@disco/utils/notifications'
import mixpanel from '@disco/api/mixpanel'

interface BriefSettingsModalProps {
  briefId: string
  onClose: () => void
  emailNotificationEnabled?: boolean
  notifyUserIds?: number[]
  notificationsLoading?: boolean
}

const BriefSettingsModal: React.FC<BriefSettingsModalProps> = ({
  briefId,
  onClose,
  emailNotificationEnabled = false,
  notifyUserIds = [],
  notificationsLoading,
}) => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState<string>('notifications')

  const handleTabChange = useCallback(
    (newTab: string) => {
      setActiveTab(newTab)
    },
    [setActiveTab],
  )

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose()
      }
    },
    [onClose],
  )

  const handleSaveNotifications = useCallback(
    async (data: NotificationFormData) => {
      try {
        const result = await dispatch(
          saveBriefSettings({
            briefId: parseInt(briefId),
            enable_email_notifications: data.emailEnabled,
            notify_user_ids: data.notifyUserIds,
          }),
        )

        if (!('error' in result.payload)) {
          mixpanel.sendPureEvent('Brief notifications toggled', {
            Context: 'Briefs',
            brief_id: briefId,
            enabled: data.emailEnabled,
          })
          pushUINotification({
            title: 'Brief settings updated',
            message: 'Brief settings updated successfully.',
            level: 'success',
          })
        }
      } catch (error) {
        // error handled by reducer
      }
      onClose()
    },
    [dispatch, briefId, onClose],
  )

  return (
    <>
      <Dialog.Root defaultOpen onOpenChange={handleDialogOpenChange}>
        <Dialog.Content
          maxWidth="600px"
          aria-describedby={undefined}
          className={styles.dialogContent}
        >
          <Dialog.Title>Brief Settings</Dialog.Title>

          <Tabs.Root
            value={activeTab}
            onValueChange={handleTabChange}
            className={styles.tabsRoot}
          >
            <Tabs.List className={styles.tabsList}>
              <Tabs.Trigger value="notifications">Notifications</Tabs.Trigger>
              <Tabs.Trigger value="notes">Notes</Tabs.Trigger>
            </Tabs.List>

            <Flex
              pt="4"
              flexGrow="1"
              minHeight="0"
              direction="column"
              className={styles.tabContentBox}
            >
              <Tabs.Content value="notifications" className={styles.tabContent}>
                {notificationsLoading ? (
                  <Text size="2" ml="2">
                    Loading...
                  </Text>
                ) : (
                  <NotificationsTab
                    onCancel={onClose}
                    onSave={handleSaveNotifications}
                    emailEnabled={emailNotificationEnabled}
                    notifyUserIds={notifyUserIds}
                  />
                )}
              </Tabs.Content>

              <Tabs.Content value="notes" className={styles.tabContent}>
                <BriefNotes briefId={briefId} handleCancel={onClose} />
              </Tabs.Content>
            </Flex>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Root>
    </>
  )
}

export default BriefSettingsModal
